import {Grid} from '@material-ui/core';
import {useQuoteField} from 'Quoting/components/hooks/useHandleQuoteFieldChange';
import {useQuoteFormValidation} from 'Quoting/components/hooks/useQuoteFormValidation';
import {A360ProductCd} from 'Quoting/models/Quoting';
import {Map as ImmutableMap} from 'immutable';
import React from 'react';
import {useSelector} from 'react-redux';
import {ReducerState} from 'rootReducer';
import {FormRowGrid, GridRowProps} from '../../../../shared/GridRow';
import {QuoteFormElement} from '../../../../shared/QuoteFormElement';
import {FormWrapper} from '../../../../shared/QuoteFormWrapper';
import {getFundingStructurePropertyPaths} from './PropertyPaths';

export const FundingStructureBlock = () => {
  const {getQuoteFieldValue, updateQuoteFieldValue} = useQuoteField();
  const {selectedProduct, selectedPlan, quoteFormErrors} = useSelector((state: ReducerState) => state.quoteForm);
  const productCd = selectedProduct?.code ?? '';

  const payrollFrequencySearchKey =
    productCd === A360ProductCd.ACC
      ? `${productCd}FundingStructure.payrollFrequencyCd`
      : `${productCd}FundingStructure.payrollFrequency`;

  const fundingStructureProperties = getFundingStructurePropertyPaths(
    selectedPlan?.planIndex,
    0,
    selectedProduct?.code
  );

  const hasError =
    Boolean(quoteFormErrors?.[payrollFrequencySearchKey]) &&
    !getQuoteFieldValue<number>(fundingStructureProperties.payrollFrequencyCd);

  const isGCI5Plan =
    getQuoteFieldValue<ImmutableMap<string, any>>(`benefitPackagingDetail.plans[${selectedPlan?.planIndex ?? 0}]`)?.get(
      'planCd'
    ) === 'GCI5';

  const handleValidationOnBlur = useQuoteFormValidation();

  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'funding-structure-row-1'},
      items: [
        {
          key: fundingStructureProperties.contributionTypeCd,
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              searchKey={`${productCd}FundingStructure.contributionTypeCd`}
              label="Contribution Type"
              value={getQuoteFieldValue(fundingStructureProperties.contributionTypeCd)}
              id={fundingStructureProperties.contributionTypeCd}
              onChange={e =>
                updateQuoteFieldValue(fundingStructureProperties.contributionTypeCd, e.target.value as string)
              }
              disabled
              required
            />
          ),
          props: {xs: 6, sm: 6, md: 6},
        },
        {
          key: fundingStructureProperties.contributionBasisCd,
          hidden: getQuoteFieldValue(fundingStructureProperties.contributionTypeCd) !== 'contributory',
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              searchKey={`${productCd}FundingStructure.contributionBasisCd`}
              label="Contribution Basis"
              value={getQuoteFieldValue<string>(fundingStructureProperties.contributionBasisCd)}
              id={fundingStructureProperties.contributionBasisCd}
              onChange={e =>
                updateQuoteFieldValue(fundingStructureProperties.contributionBasisCd, e.target.value as string)
              }
              disabled
              required
            />
          ),
          props: {xs: 6, sm: 6, md: 6},
        },
        {
          key: fundingStructureProperties.minParticipantsNum,
          hidden: getQuoteFieldValue<string>(fundingStructureProperties.contributionTypeCd) === 'contributory',
          element: (
            <QuoteFormElement
              elementType="Text"
              type="number"
              label="Minimum Number of Participants"
              value={getQuoteFieldValue<number>(fundingStructureProperties.minParticipantsNum)}
              id={fundingStructureProperties.minParticipantsNum}
              onChange={e => updateQuoteFieldValue(fundingStructureProperties.minParticipantsNum, e.target.value)}
              disabled
              required
            />
          ),
          props: {xs: 6, sm: 6, md: 6},
        },
        {
          key: fundingStructureProperties.groupContributionPct,
          hidden: getQuoteFieldValue<string>(fundingStructureProperties.contributionTypeCd) !== 'contributory',
          element: (
            <>
              {
                <>
                  {getQuoteFieldValue<string>(fundingStructureProperties.contributionBasisCd) === 'Percent' ? (
                    <QuoteFormElement
                      elementType="Text"
                      label="Group Contribution %"
                      value={getQuoteFieldValue<string>(fundingStructureProperties.groupContributionPct)}
                      id={fundingStructureProperties.groupContributionPct}
                      onChange={e =>
                        updateQuoteFieldValue(fundingStructureProperties.groupContributionPct, e.target.value)
                      }
                    />
                  ) : (
                    <QuoteFormElement
                      elementType="Text"
                      label="Group Premium Annual Contribution Amount"
                      type="number"
                      value={getQuoteFieldValue<number>(fundingStructureProperties.sponsorContributionAmount)}
                      id={fundingStructureProperties.sponsorContributionAmount}
                      onChange={e =>
                        updateQuoteFieldValue(fundingStructureProperties.sponsorContributionAmount, {
                          amount: e.target.value,
                          currency: 'USD',
                        })
                      }
                      disabled
                    />
                  )}
                </>
              }
            </>
          ),
          props: {xs: 6, sm: 6, md: 6},
        },
        {
          key: fundingStructureProperties.paidBenefitAppliesToCd,
          hidden: getQuoteFieldValue<string>(fundingStructureProperties.contributionTypeCd) !== 'contributory',
          element: (
            <QuoteFormElement
              elementType="Text"
              type="number"
              label="Group Paid Portion Applies to"
              value={getQuoteFieldValue<number>(fundingStructureProperties.paidBenefitAppliesToCd)}
              id={fundingStructureProperties.paidBenefitAppliesToCd}
              onChange={e => updateQuoteFieldValue(fundingStructureProperties.paidBenefitAppliesToCd, e.target.value)}
              disabled
            />
          ),
          props: {xs: 6, sm: 6, md: 6},
        },
        {
          key: fundingStructureProperties.minParticipantsNum,
          hidden: getQuoteFieldValue<string>(fundingStructureProperties.contributionTypeCd) !== 'contributory',
          element: (
            <QuoteFormElement
              elementType="Text"
              type="number"
              label="Minimum Number of Participants"
              value={getQuoteFieldValue<number>(fundingStructureProperties.minParticipantsNum)}
              id={fundingStructureProperties.minParticipantsNum}
              onChange={e => updateQuoteFieldValue(fundingStructureProperties.minParticipantsNum, e.target.value)}
              disabled
              required
            />
          ),
          props: {xs: 6, sm: 6, md: 6},
        },
        {
          key: fundingStructureProperties.requiredParticipationPct,
          hidden: !isGCI5Plan,
          element: (
            <QuoteFormElement
              elementType="Text"
              type="number"
              label="Minimum Participation %"
              value={getQuoteFieldValue<number>(fundingStructureProperties.requiredParticipationPct)}
              id={fundingStructureProperties.requiredParticipationPct}
              onChange={e =>
                updateQuoteFieldValue(fundingStructureProperties.requiredParticipationPct, Number(e.target.value) / 100)
              }
              disabled
              adornment="percentage"
            />
          ),
          props: {xs: 6, sm: 6, md: 6},
        },
        {
          key: fundingStructureProperties.requiredGroupAccessPct,
          hidden: ![A360ProductCd.HI, A360ProductCd.ACC].includes(productCd as A360ProductCd),
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              searchKey={`${productCd}FundingStructure.requiredGroupAccessPct`}
              label="Required Group Access %"
              value={getQuoteFieldValue<string>(fundingStructureProperties.requiredGroupAccessPct)}
              id={fundingStructureProperties.requiredGroupAccessPct}
              onChange={e =>
                updateQuoteFieldValue(fundingStructureProperties.requiredGroupAccessPct, e.target.value as string)
              }
              disabled
              required={productCd !== A360ProductCd.HI}
            />
          ),
          props: {xs: 6, sm: 6, md: 6},
        },
        {
          key: fundingStructureProperties.requiredGroupAccessPctCd,
          hidden: !isGCI5Plan,
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              searchKey={`${productCd}FundingStructure.requiredGroupAccessPctCd`}
              label="Required Group Access %"
              value={getQuoteFieldValue<string>(fundingStructureProperties.requiredGroupAccessPctCd)}
              id={fundingStructureProperties.requiredGroupAccessPctCd}
              onChange={e =>
                updateQuoteFieldValue(fundingStructureProperties.requiredGroupAccessPctCd, e.target.value as string)
              }
              disabled
              required
            />
          ),
          props: {xs: 6, sm: 6, md: 6},
        },
        {
          key: fundingStructureProperties.payrollFrequencyCd,
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              searchKey={payrollFrequencySearchKey}
              label="Payroll Frequency"
              id={fundingStructureProperties.payrollFrequencyCd}
              value={getQuoteFieldValue<string>(fundingStructureProperties.payrollFrequencyCd)}
              onChange={e =>
                updateQuoteFieldValue(fundingStructureProperties.payrollFrequencyCd, e.target.value as string)
              }
              onBlur={() => handleValidationOnBlur()}
              error={hasError}
              helperText={hasError ? quoteFormErrors[payrollFrequencySearchKey] : undefined}
              required
            />
          ),
          props: {xs: 6, sm: 6, md: 6},
        },
        {
          key: fundingStructureProperties.fundingTypeCd,
          hidden: true,
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              searchKey={`${productCd}FundingStructure.fundingTypeCd`}
              label="Funding Type"
              id={fundingStructureProperties.fundingTypeCd}
              value={getQuoteFieldValue<string>(fundingStructureProperties.fundingTypeCd)}
              onChange={e => updateQuoteFieldValue(fundingStructureProperties.fundingTypeCd, e.target.value as string)}
              disabled
            />
          ),
          props: {xs: 6, sm: 6, md: 6},
        },
        {
          key: fundingStructureProperties.taxabilityCd,
          hidden: true,
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              searchKey={`${productCd}FundingStructure.taxabilityCd`}
              label="Taxability"
              id={fundingStructureProperties.taxabilityCd}
              value={getQuoteFieldValue<string>(fundingStructureProperties.taxabilityCd)}
              onChange={e => updateQuoteFieldValue(fundingStructureProperties.taxabilityCd, e.target.value as string)}
              disabled
            />
          ),
          props: {xs: 6, sm: 6, md: 6},
        },
      ],
    },
  ];

  return (
    <FormWrapper title="Funding Structure" collapsible withSeparator expanded>
      <Grid container alignItems="center">
        <FormRowGrid rows={rows} />
      </Grid>
    </FormWrapper>
  );
};
